/**
 * Mode spectateur de la phase de dessin.
 *
 * Pour qui n'a pas de role cette manche : l'arrivant d'une partie rapide en
 * cours, et l'elimine du battle royale. Sans lui, ces joueurs regardaient un
 * compte a rebours tourner sur un ecran vide, ce qui est la meilleure facon de
 * faire fermer un onglet.
 *
 * Le direct n'est PAS un flux de traits : le serveur demande a l'auteur regarde
 * un instantane toutes les deux secondes, et a lui seul (cf. CLAUDE.md). Le
 * dessin arrive donc par a-coups, ce qui suffit largement a voir une retouche
 * se construire.
 */
import { useEffect, type ReactElement } from 'react';
import type { PlayerView, RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { useRoomStore } from '../store/roomStore.js';
import { Avatar } from '../ui/Avatar.js';
import { BotTag } from '../ui/BotTag.js';
import { SpectateBoard } from './SpectateBoard.js';
import { PhaseTimer } from '../ui/PhaseTimer.js';
import { EyeIcon } from '../ui/icons.js';

export function SpectatorView({
  room,
  selfId,
  durationMs,
}: {
  room: RoomSnapshot;
  selfId: string | null;
  durationMs: number;
}): ReactElement {
  const t = useT();
  const spectate = useRoomStore((state) => state.spectate);

  /**
   * Qui peut etre regarde : ceux qui jouent la manche. Un autre spectateur n'a
   * rien a montrer, et se regarder soi-meme n'a pas de sens.
   */
  const watchable = room.players.filter(
    (player) => player.id !== selfId && !player.eliminated && !player.waiting,
  );
  const target = spectate?.target ?? null;

  // Une cible d'office : arriver sur une liste de noms sans rien a l'ecran
  // demanderait de deviner qu'il faut cliquer.
  useEffect(() => {
    if (target !== null) return;
    const first = watchable[0];
    if (first === undefined) return;
    connection.send({ type: 'spectate', target: first.id });
    // `watchable` change d'identite a chaque snapshot : on ne depend que du
    // premier identifiant, sinon l'effet repartirait en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, watchable[0]?.id]);

  const watched = room.players.find((player) => player.id === target) ?? null;

  return (
    <main className="watching spectator">
      <div className="watching__timer">
        <PhaseTimer endsAt={room.endsAt} durationMs={durationMs} size={64} />
      </div>

      <p className="watching__title">
        {watched === null
          ? t('spectator.title')
          : t('spectator.watching', { name: watched.nickname })}
      </p>
      <p className="watching__subtitle">{t('spectator.hint')}</p>

      {spectate !== null && spectate.file !== null && spectate.zone !== null ? (
        <div className="spectator__stage">
          <SpectateBoard
            // Le tableau change de joueur en joueur : sans cette cle, le canvas
            // garderait le rendu du precedent jusqu'au prochain instantane.
            key={`${spectate.file}-${spectate.slot}`}
            file={spectate.file}
            zone={spectate.zone}
            palette={spectate.palette}
            drawing={spectate.drawing}
          />
        </div>
      ) : (
        <p className="spectator__empty">{t('spectator.nothing')}</p>
      )}

      <div className="watching__players">
        {watchable.map((player) => (
          <SpectatorPick
            key={player.id}
            player={player}
            selected={player.id === target}
            onSelect={() => connection.send({ type: 'spectate', target: player.id })}
          />
        ))}
      </div>
    </main>
  );
}

function SpectatorPick({
  player,
  selected,
  onSelect,
}: {
  player: PlayerView;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  const t = useT();
  return (
    <button
      type="button"
      className="watching__player spectator__pick"
      aria-pressed={selected}
      aria-label={t('spectator.watch', { name: player.nickname })}
      onClick={onSelect}
    >
      <Avatar
        playerId={player.id} avatar={player.avatar}
        colorIndex={player.colorIndex}
        size={40}
        dimmed={!player.connected}
        done={player.done}
      />
      <span className="watching__name">
        {/* L'œil a GAUCHE du pseudo : c'est lui qu'on cherche du regard quand on
            veut savoir qui l'on suit, pas le nom. */}
        {selected && (
          <span className="spectator__eye">
            <EyeIcon size={12} />
          </span>
        )}
        {player.nickname}
        {player.bot && <BotTag size={12} />}
      </span>
    </button>
  );
}
